import { useContext } from "react";
import { useForm } from "react-hook-form";
import { WORKFLOW_STEP_MANUAL_STATUS } from "common/constants";
import {
  useUpdateWorkflowStepStatusMutation,
  WorkflowStepManualStatus,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { FlexColumn } from "../Common/__styles__/Layout";
import { CommonModal } from "../Common/CommonModal";
import { Tag } from "../Common/Tag";
import Radio from "../Inputs/Radio";
import { getWorkflowStepManualStatusDisplay } from "./workflowUtils";

const HIDDEN_STATUSES = new Set([
  WORKFLOW_STEP_MANUAL_STATUS.NOT_STARTED,
  WORKFLOW_STEP_MANUAL_STATUS.IN_PROGRESS,
]);

const normalizeStatus = (
  status: WORKFLOW_STEP_MANUAL_STATUS,
): WORKFLOW_STEP_MANUAL_STATUS => {
  if (status === WORKFLOW_STEP_MANUAL_STATUS.NOT_STARTED) {
    return WORKFLOW_STEP_MANUAL_STATUS.LOCKED;
  }
  if (status === WORKFLOW_STEP_MANUAL_STATUS.IN_PROGRESS) {
    return WORKFLOW_STEP_MANUAL_STATUS.OPEN;
  }
  return status;
};

const STATUS_OPTIONS = Object.values(WORKFLOW_STEP_MANUAL_STATUS)
  .filter(status => !HIDDEN_STATUSES.has(status))
  .map(status => {
    const { text, variant } = getWorkflowStepManualStatusDisplay(status);
    return {
      value: status,
      text: <Tag styleVariant={variant}>{text}</Tag>,
    };
  });

interface FormData {
  status: WorkflowStepManualStatus;
}

interface UpdateWorkflowStepStatusModalProps {
  workflowStepId: string;
  currentStatus: WorkflowStepManualStatus;
  submissionTypeName: string;
  onCancel: () => void;
  onSuccess: () => void;
}

export const UpdateWorkflowStepStatusModal = ({
  workflowStepId,
  currentStatus,
  submissionTypeName,
  onCancel,
  onSuccess,
}: UpdateWorkflowStepStatusModalProps) => {
  const { addErrorToast, addSuccessToast } = useStatusToasts();

  const { user, admin } = useContext(AuthContext);

  const { watch, setValue } = useForm<FormData>({
    defaultValues: {
      status: normalizeStatus(currentStatus),
    },
  });

  const selectedStatus = watch("status");

  const [updateStatus, { loading }] = useUpdateWorkflowStepStatusMutation({
    onCompleted: () => {
      track("Updated Workflow Step Status", {
        workflowStepId,
        oldStatus: currentStatus,
        newStatus: selectedStatus,
        userId: user?.id ?? admin?.id,
        userEmail: user?.email ?? admin?.email,
      });
      addSuccessToast("Status updated successfully");
      onSuccess();
    },
    onError: error => {
      addErrorToast(`Failed to update status: ${error.message}`);
    },
    refetchQueries: ["GetWorkflow"],
  });

  const onSubmit = async () => {
    await updateStatus({
      variables: {
        workflowStepId,
        status: selectedStatus,
      },
    });
  };

  const content = (
    <FlexColumn gap="m">
      <Radio
        name="status"
        value={selectedStatus}
        options={STATUS_OPTIONS}
        onChange={value =>
          setValue("status", value as WorkflowStepManualStatus)
        }
        autoFocus
      />
    </FlexColumn>
  );

  return (
    <CommonModal
      title="Update status"
      subtitle={submissionTypeName}
      content={content}
      onSubmit={onSubmit}
      onCancel={onCancel}
      loading={loading}
      disabled={loading}
      primaryButtonText="Save"
      size="small"
      dataTestId="update-workflow-step-status-modal"
    />
  );
};
